import dayjs, { type Dayjs } from 'dayjs';
import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import {
  Autocomplete,
  Box,
  Button,
  Checkbox,
  Chip,
  LinearProgress,
  Skeleton,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TableSortLabel,
  TextField,
  Tooltip,
  Typography,
  type SxProps,
  type Theme,
} from '@mui/material';
import { Clear, Download, Refresh } from '@mui/icons-material';
import { alpha, useTheme } from '@mui/material/styles';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { useTranslation } from 'react-i18next';

import FilteredListLayout from '../FilteredListLayout';
import LinkPagination from '../LinkPagination';
import LinkTableCell from '../LinkTableCell';
import ListActionButton from '../ListActionButton';
import { useLanguage } from '../../hooks/useLanguage';
import { useCampaignReportWebSocket, type CampaignReportObserverEvent } from '../../hooks/useCampaignReportWebSocket';
import { useListSearchParams } from '../../hooks/useListSearchParams';
import { useNumberFormat } from '../../hooks/useNumberFormat';
import { useWebSocket } from '../../contexts/WebSocketContext';
import {
  fetchCampaignReportCampaignOptions,
  fetchCampaignReports,
  requestCampaignReportDownload,
} from '../../services/reports';
import { formatDateTime } from '../../utils/date';

import type { CampaignReportListItem, CampaignReportStatus, UUID } from '../../types';


interface ReportStatusOption {
  value: CampaignReportStatus;
  label: string;
}

interface ReportCampaignOption {
  value: UUID;
  label: string;
}

type ReportDateRangePreset = 'today' | 'yesterday' | 'last_7_days' | 'period';

const REPORT_STATUS_VALUES: readonly CampaignReportStatus[] = ['pending', 'processing', 'completed', 'failed'];
const REPORT_MULTI_VALUE_SEPARATOR = ',';
const REPORT_CAMPAIGN_AUTOCOMPLETE_LIMIT = 10;
const REPORT_CAMPAIGN_AUTOCOMPLETE_DEBOUNCE_MS = 300;
const FILTER_MIN_WIDTH_STATUS = 240;
const FILTER_MIN_WIDTH_CAMPAIGN = 260;
const FILTER_MIN_WIDTH_DATE_RANGE = 220;
const DATE_FILTER_PARAM_FORMAT = 'YYYY-MM-DD';
const DATE_TIME_FILTER_PARAM_FORMAT = 'YYYY-MM-DDTHH:mm:ss';
const REPORT_DATE_RANGE_PRESETS: readonly ReportDateRangePreset[] = ['today', 'yesterday', 'last_7_days', 'period'];

interface ReportDateRangeOption {
  value: ReportDateRangePreset;
  label: string;
}

function buildFilterFieldSx(minWidth: number): SxProps<Theme> {
  return {
    minWidth,
    flex: `1 1 ${minWidth}px`,
  };
}

function isReportDateRangePreset(value: string | null | undefined): value is ReportDateRangePreset {
  if (!value) {
    return false;
  }
  return REPORT_DATE_RANGE_PRESETS.includes(value as ReportDateRangePreset);
}

function parseMultiFilterValue(rawValue: string | null | undefined): string[] {
  if (!rawValue) {
    return [];
  }
  const normalizedValues = rawValue
    .split(REPORT_MULTI_VALUE_SEPARATOR)
    .map((value) => value.trim())
    .filter(Boolean);
  return Array.from(new Set(normalizedValues));
}

function serializeMultiFilterValue(values: string[]): string | undefined {
  const normalizedValues = values
    .map((value) => value.trim())
    .filter(Boolean);
  if (normalizedValues.length === 0) {
    return undefined;
  }
  return normalizedValues.join(REPORT_MULTI_VALUE_SEPARATOR);
}

function getReportStatusColor(
  status: CampaignReportStatus,
): 'default' | 'warning' | 'success' | 'error' {
  if (status === 'completed') {
    return 'success';
  }
  if (status === 'failed') {
    return 'error';
  }
  if (status === 'processing') {
    return 'warning';
  }
  return 'default';
}

function applyReportEvent(
  report: CampaignReportListItem,
  event: CampaignReportObserverEvent,
): CampaignReportListItem {
  return {
    ...report,
    status: event.status,
    startedAt: event.started_at ?? report.startedAt,
    completedAt: event.completed_at ?? report.completedAt,
    fileName: event.file_name ?? report.fileName,
    fileSize: event.file_size ?? report.fileSize,
    totalContacts: event.total_contacts ?? report.totalContacts,
    totalCalls: event.total_calls ?? report.totalCalls,
    progress: event.progress ?? report.progress,
    processedContacts: event.processed_contacts ?? report.processedContacts,
    processedCalls: event.processed_calls ?? report.processedCalls,
    estimatedSecondsRemaining: event.estimated_seconds_remaining ?? report.estimatedSecondsRemaining,
    progressMessage: event.progress_message ?? report.progressMessage,
    errorMessage: event.error_message ?? report.errorMessage,
    downloadUrl: event.file_url ?? report.downloadUrl,
  };
}

function CampaignReportLiveObserver({
  reportId,
  onEvent,
  onReady,
  onFailed,
}: {
  reportId: string;
  onEvent: (event: CampaignReportObserverEvent) => void;
  onReady: (event: CampaignReportObserverEvent) => void;
  onFailed: (event: CampaignReportObserverEvent) => void;
}) {
  useCampaignReportWebSocket(reportId, {
    onReportUpdated: onEvent,
    onReportReady: onReady,
    onReportFailed: onFailed,
  });
  return null;
}

const REPORT_BODY_CELL_SX: SxProps<Theme> = {
  verticalAlign: 'middle',
};

const REPORT_DOWNLOAD_HEADER_CELL_SX: SxProps<Theme> = {
  textAlign: 'center',
};

const REPORT_DOWNLOAD_CELL_SX: SxProps<Theme> = {
  ...REPORT_BODY_CELL_SX,
  textAlign: 'center',
};

const REPORT_DOWNLOAD_BOX_SX: SxProps<Theme> = {
  display: 'inline-flex',
  flexDirection: 'column',
  alignItems: 'center',
  gap: 0.5,
};

export default function CampaignReportsTab() {
  const { t } = useTranslation(['pages', 'common']);
  const { language } = useLanguage();
  const theme = useTheme();
  const isDarkMode = theme.palette.mode === 'dark';
  const tableRowHoverBg = alpha(theme.palette.primary.main, 0.08);
  const reportHeaderRowSx: SxProps<Theme> = useMemo(() => ({
    bgcolor: alpha(theme.palette.primary.main, 0.08),
    '& th': {
      textTransform: 'uppercase',
      letterSpacing: '0.05em',
      fontSize: '0.75rem',
      whiteSpace: 'nowrap',
      textAlign: 'left',
      verticalAlign: 'middle',
    },
    '& .MuiTableSortLabel-root': {
      whiteSpace: 'nowrap',
    },
  }), [theme]);
  const dayjsLocale = useMemo(() => {
    const localeMap: Record<string, string> = {
      'pt-BR': 'pt-br',
      en: 'en',
      es: 'es',
    };
    return localeMap[language] || 'en';
  }, [language]);
  const filterInputSx = {
    borderRadius: '10px',
    backgroundColor: isDarkMode ? 'rgba(255,255,255,0.04)' : '#ffffff',
    '& .MuiOutlinedInput-notchedOutline': {
      borderColor: isDarkMode ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.08)',
    },
    '& .MuiSelect-select': {
      padding: '11px 12px',
    },
    '& .MuiInputBase-input': {
      padding: '11px 12px',
    },
    '& .MuiPickersInputBase-root': {
      padding: '3px 14px 2px 14px',
    },
  };
  const {
    formatNumber,
    formatPercent,
    formatDecimal,
  } = useNumberFormat();
  const { state: webSocketState } = useWebSocket();

  const [reports, setReports] = useState<CampaignReportListItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [totalReports, setTotalReports] = useState(0);
  const [campaignOptions, setCampaignOptions] = useState<ReportCampaignOption[]>([]);
  const [campaignSearchInput, setCampaignSearchInput] = useState('');
  const [isCampaignLoading, setIsCampaignLoading] = useState(false);
  const [isCampaignOpen, setIsCampaignOpen] = useState(false);
  const [downloadingReportId, setDownloadingReportId] = useState<string | null>(null);
  const campaignRequestIdRef = useRef(0);

  const {
    filters,
    page,
    rowsPerPage,
    ordering,
    setParams,
    setPage,
    setRowsPerPage,
    getPageLink,
  } = useListSearchParams({
    filters: ['campaign', 'status', 'date_range', 'start_date', 'end_date'],
    defaultPage: 1,
    defaultPageSize: 25,
    defaultOrdering: '-created_at',
  });

  const campaignFilterValue = filters.campaign ?? '';
  const statusFilterValues = useMemo(() => parseMultiFilterValue(filters.status), [filters.status]);
  const startDateFilterValue = filters.start_date ?? '';
  const endDateFilterValue = filters.end_date ?? '';
  const selectedDateRange = useMemo<ReportDateRangePreset | null>(() => {
    if (isReportDateRangePreset(filters.date_range)) {
      return filters.date_range;
    }
    if (startDateFilterValue || endDateFilterValue) {
      return 'period';
    }
    return null;
  }, [endDateFilterValue, filters.date_range, startDateFilterValue]);
  const dateRangeOptions = useMemo<ReportDateRangeOption[]>(
    () => [
      { value: 'today', label: t('pages:calls.filters.today') },
      { value: 'yesterday', label: t('pages:calls.filters.yesterday') },
      { value: 'last_7_days', label: t('pages:calls.filters.last7Days') },
      { value: 'period', label: t('pages:calls.filters.period') },
    ],
    [t],
  );
  const dateRangeOption = useMemo(
    () => dateRangeOptions.find((option) => option.value === selectedDateRange) ?? null,
    [dateRangeOptions, selectedDateRange],
  );
  const startDateValue = useMemo<Dayjs | null>(() => {
    if (!startDateFilterValue) {
      return null;
    }
    const value = dayjs(startDateFilterValue);
    return value.isValid() ? value : null;
  }, [startDateFilterValue]);
  const endDateValue = useMemo<Dayjs | null>(() => {
    if (!endDateFilterValue) {
      return null;
    }
    const value = dayjs(endDateFilterValue);
    return value.isValid() ? value : null;
  }, [endDateFilterValue]);
  const resolvedDateParams = useMemo(() => {
    if (selectedDateRange === 'period') {
      return {
        startDate: startDateFilterValue || undefined,
        endDate: endDateFilterValue || undefined,
      };
    }

    if (!selectedDateRange) {
      return { startDate: undefined, endDate: undefined };
    }

    const now = dayjs();
    if (selectedDateRange === 'today') {
      const value = now.format(DATE_FILTER_PARAM_FORMAT);
      return { startDate: value, endDate: value };
    }
    if (selectedDateRange === 'yesterday') {
      const value = now.subtract(1, 'day').format(DATE_FILTER_PARAM_FORMAT);
      return { startDate: value, endDate: value };
    }

    return {
      startDate: now.subtract(6, 'day').format(DATE_FILTER_PARAM_FORMAT),
      endDate: now.format(DATE_FILTER_PARAM_FORMAT),
    };
  }, [endDateFilterValue, selectedDateRange, startDateFilterValue]);

  const statusOptions = useMemo<ReportStatusOption[]>(
    () => REPORT_STATUS_VALUES.map((value) => ({
      value,
      label: t(`common:status.${value}`),
    })),
    [t],
  );

  const selectedStatusOptions = useMemo(
    () =>
      statusFilterValues
        .map((value) => statusOptions.find((option) => option.value === value))
        .filter((option): option is ReportStatusOption => Boolean(option)),
    [statusFilterValues, statusOptions],
  );
  const selectedCampaignOption = useMemo<ReportCampaignOption | null>(() => {
    if (!campaignFilterValue) {
      return null;
    }
    const existingOption = campaignOptions.find((option) => option.value === campaignFilterValue);
    if (existingOption) {
      return existingOption;
    }
    const matchingReport = reports.find((report) => report.campaignId === campaignFilterValue);
    return {
      value: campaignFilterValue,
      label: matchingReport?.campaignName || campaignFilterValue,
    };
  }, [campaignFilterValue, campaignOptions, reports]);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const response = await fetchCampaignReports({
        page: page + 1,
        pageSize: rowsPerPage,
        ordering,
        campaign: campaignFilterValue || undefined,
        status: filters.status || undefined,
        startDate: resolvedDateParams.startDate,
        endDate: resolvedDateParams.endDate,
      });
      setReports(response.results);
      setTotalReports(response.count);
    } finally {
      setLoading(false);
    }
  }, [campaignFilterValue, filters.status, ordering, page, resolvedDateParams.endDate, resolvedDateParams.startDate, rowsPerPage]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  useEffect(() => {
    if (!isCampaignOpen) {
      return undefined;
    }

    const timeoutId = window.setTimeout(async () => {
      const requestId = campaignRequestIdRef.current + 1;
      campaignRequestIdRef.current = requestId;
      setIsCampaignLoading(true);

      try {
        const response = await fetchCampaignReportCampaignOptions({
          search: campaignSearchInput.trim() || undefined,
          selected: campaignFilterValue || undefined,
          limit: REPORT_CAMPAIGN_AUTOCOMPLETE_LIMIT,
        });
        if (campaignRequestIdRef.current !== requestId) {
          return;
        }

        const nextOptions = response.map((campaign) => ({
          value: campaign.id,
          label: campaign.name || campaign.id,
        }));
        setCampaignOptions(nextOptions);
      } finally {
        if (campaignRequestIdRef.current === requestId) {
          setIsCampaignLoading(false);
        }
      }
    }, REPORT_CAMPAIGN_AUTOCOMPLETE_DEBOUNCE_MS);

    return () => window.clearTimeout(timeoutId);
  }, [campaignFilterValue, campaignSearchInput, isCampaignOpen]);

  const handleStatusChange = useCallback(
    (_event: unknown, values: ReportStatusOption[]) => {
      setParams(
        { status: serializeMultiFilterValue(values.map((value) => value.value)) },
        { resetPage: true },
      );
    },
    [setParams],
  );

  const handleClearFilters = useCallback(() => {
    setCampaignSearchInput('');
    setParams(
      { campaign: undefined, status: undefined, date_range: undefined, start_date: undefined, end_date: undefined },
      { resetPage: true },
    );
  }, [setParams]);

  const handleSortToggle = useCallback(
    (field: string) => {
      const nextOrdering = ordering === field ? `-${field}` : field;
      setParams({ ordering: nextOrdering }, { resetPage: true });
    },
    [ordering, setParams],
  );

  const handleLiveEvent = useCallback((event: CampaignReportObserverEvent) => {
    setReports((current) =>
      current.map((report) => (
        report.id === event.id
          ? applyReportEvent(report, event)
          : report
      )),
    );
  }, []);

  const handleLiveTerminal = useCallback((event: CampaignReportObserverEvent) => {
    handleLiveEvent(event);
    void fetchData();
  }, [fetchData, handleLiveEvent]);

  const handleDownload = useCallback(async (reportId: string) => {
    try {
      setDownloadingReportId(reportId);
      const downloadUrl = await requestCampaignReportDownload(reportId);
      if (downloadUrl) {
        window.open(downloadUrl, '_blank', 'noopener,noreferrer');
      }
    } catch (error) {
      console.error('Error requesting campaign report download:', error);
    } finally {
      setDownloadingReportId((current) => (current === reportId ? null : current));
    }
  }, []);

  const activeReportIds = useMemo(
    () => reports
      .filter((report) => report.status === 'pending' || report.status === 'processing')
      .map((report) => report.id),
    [reports],
  );
  const activeReportIdsSignature = useMemo(
    () => activeReportIds.join(','),
    [activeReportIds],
  );
  const reconciledActiveReportSignatureRef = useRef('');

  useEffect(() => {
    if (!webSocketState.isConnected || activeReportIds.length === 0) {
      if (activeReportIds.length === 0) {
        reconciledActiveReportSignatureRef.current = '';
      }
      return;
    }
    if (reconciledActiveReportSignatureRef.current === activeReportIdsSignature) {
      return;
    }
    reconciledActiveReportSignatureRef.current = activeReportIdsSignature;
    void fetchData();
  }, [activeReportIds.length, activeReportIdsSignature, fetchData, webSocketState.isConnected]);

  const table = (
    <>
      {activeReportIds.map((reportId) => (
        <CampaignReportLiveObserver
          key={reportId}
          reportId={reportId}
          onEvent={handleLiveEvent}
          onReady={handleLiveTerminal}
          onFailed={handleLiveTerminal}
        />
      ))}
      <Table>
        <TableHead>
          <TableRow sx={reportHeaderRowSx}>
            <TableCell sx={{ textAlign: 'left' }}>{t('pages:reports.campaigns.columns.campaign')}</TableCell>
            <TableCell sx={{ textAlign: 'left' }}>
              <TableSortLabel
                active={ordering === 'created_at' || ordering === '-created_at'}
                direction={ordering === 'created_at' ? 'asc' : 'desc'}
                onClick={() => handleSortToggle('created_at')}
                sx={{ whiteSpace: 'nowrap' }}
              >
                {t('pages:reports.campaigns.columns.createdAt')}
              </TableSortLabel>
            </TableCell>
            <TableCell sx={{ textAlign: 'left' }}>{t('pages:reports.campaigns.columns.createdBy')}</TableCell>
            <TableCell sx={{ textAlign: 'left' }}>{t('pages:reports.campaigns.columns.volume')}</TableCell>
            <TableCell sx={{ textAlign: 'left' }}>{t('pages:reports.campaigns.columns.status')}</TableCell>
            <TableCell sx={REPORT_DOWNLOAD_HEADER_CELL_SX}>{t('pages:reports.campaigns.columns.download')}</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {loading ? (
            Array.from({ length: 6 }).map((_, index) => (
              <TableRow key={`campaign-report-skeleton-${index}`}>
                {Array.from({ length: 6 }).map((__, cellIndex) => (
                  <TableCell
                    key={`campaign-report-skeleton-cell-${index}-${cellIndex}`}
                    sx={cellIndex === 5 ? REPORT_DOWNLOAD_CELL_SX : REPORT_BODY_CELL_SX}
                  >
                    <Skeleton variant="text" />
                  </TableCell>
                ))}
              </TableRow>
            ))
          ) : reports.length === 0 ? (
            <TableRow>
              <TableCell colSpan={6} sx={{ py: 6, textAlign: 'center' }}>
                <Typography variant="body1" color="text.secondary">
                  {t('pages:reports.campaigns.empty')}
                </Typography>
              </TableCell>
            </TableRow>
          ) : (
            reports.map((report) => {
              const showProgress = report.status === 'pending' || report.status === 'processing';
              const progressValue = Math.max(0, Math.min(report.progress ?? 0, 100));
              return (
                <TableRow
                  key={report.id}
                  hover
                  sx={{ '&:hover': { backgroundColor: tableRowHoverBg } }}
                >
                  <LinkTableCell
                    to={`/campaigns/${report.campaignId}`}
                    sx={{ ...REPORT_BODY_CELL_SX, textAlign: 'left' }}
                    linkProps={{ color: 'inherit', underline: 'hover' }}
                  >
                    <Typography variant="body2" fontWeight={600}>
                      {report.campaignName}
                    </Typography>
                  </LinkTableCell>
                  <TableCell sx={{ ...REPORT_BODY_CELL_SX, textAlign: 'left' }}>
                    <Typography variant="body2">
                      {formatDateTime(report.createdAt)}
                    </Typography>
                    {report.completedAt ? (
                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                        {t('pages:reports.campaigns.completedAt', {
                          value: formatDateTime(report.completedAt),
                        })}
                      </Typography>
                    ) : null}
                  </TableCell>
                  <TableCell sx={{ ...REPORT_BODY_CELL_SX, textAlign: 'left' }}>
                    <Typography variant="body2">
                      {report.createdByName || t('pages:reports.campaigns.unknownUser')}
                    </Typography>
                  </TableCell>
                  <TableCell sx={{ ...REPORT_BODY_CELL_SX, textAlign: 'left' }}>
                    <Typography variant="body2">
                      {t('pages:reports.campaigns.volumeValue', {
                        contacts: formatNumber(report.totalContacts),
                        calls: formatNumber(report.totalCalls),
                      })}
                    </Typography>
                    {report.processedContacts || report.processedCalls ? (
                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                        {t('pages:reports.campaigns.processedValue', {
                          contacts: formatNumber(report.processedContacts || 0),
                          calls: formatNumber(report.processedCalls || 0),
                        })}
                      </Typography>
                    ) : null}
                  </TableCell>
                  <TableCell sx={{ ...REPORT_BODY_CELL_SX, textAlign: 'left', minWidth: 240 }}>
                    <Chip
                      size="small"
                      color={getReportStatusColor(report.status)}
                      label={t(`common:status.${report.status}`)}
                    />
                    {showProgress ? (
                      <Box sx={{ mt: 1.25 }}>
                        <LinearProgress
                          variant="determinate"
                          value={progressValue}
                          sx={{ height: 8, borderRadius: 999 }}
                        />
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.75 }}>
                          {formatPercent(progressValue)}
                          {report.estimatedSecondsRemaining
                            ? ` • ${t('pages:reports.campaigns.eta', { seconds: report.estimatedSecondsRemaining })}`
                            : ''}
                        </Typography>
                        {report.progressMessage ? (
                          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.25 }}>
                            {report.progressMessage}
                          </Typography>
                        ) : null}
                      </Box>
                    ) : null}
                    {report.errorMessage ? (
                      <Tooltip title={report.errorMessage} arrow>
                        <Typography
                          variant="caption"
                          color="error.main"
                          sx={{
                            display: 'block',
                            mt: 0.75,
                            maxWidth: 220,
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                          }}
                        >
                          {report.errorMessage}
                        </Typography>
                      </Tooltip>
                    ) : null}
                  </TableCell>
                  <TableCell sx={REPORT_DOWNLOAD_CELL_SX}>
                    {report.downloadUrl ? (
                      <Box sx={REPORT_DOWNLOAD_BOX_SX}>
                        <Button
                          size="small"
                          variant="contained"
                          startIcon={<Download />}
                          disabled={downloadingReportId === report.id}
                          onClick={() => {
                            void handleDownload(report.id);
                          }}
                        >
                          {t('common:actions.download')}
                        </Button>
                        {report.fileSize ? (
                          <Typography variant="caption" color="text.secondary">
                            {t('pages:reports.campaigns.fileSize', {
                              size: formatDecimal(report.fileSize / 1024 / 1024),
                            })}
                          </Typography>
                        ) : null}
                      </Box>
                    ) : (
                      <Box sx={REPORT_DOWNLOAD_BOX_SX}>
                        <Typography variant="body2" color="text.secondary">
                          {t('pages:reports.campaigns.noDownload')}
                        </Typography>
                        {report.fileSize ? (
                          <Typography variant="caption" color="text.secondary">
                            {t('pages:reports.campaigns.fileSize', {
                              size: formatDecimal(report.fileSize / 1024 / 1024),
                            })}
                          </Typography>
                        ) : null}
                      </Box>
                    )}
                  </TableCell>
                </TableRow>
              );
            })
          )}
        </TableBody>
      </Table>
    </>
  );

  return (
    <FilteredListLayout
      filterControls={(
        <>
          <Autocomplete<ReportCampaignOption, false, false, false>
            open={isCampaignOpen}
            onOpen={() => setIsCampaignOpen(true)}
            onClose={() => setIsCampaignOpen(false)}
            options={campaignOptions}
            value={selectedCampaignOption}
            loading={isCampaignLoading}
            isOptionEqualToValue={(option, value) => option.value === value.value}
            getOptionLabel={(option) => option.label}
            inputValue={campaignSearchInput}
            onInputChange={(_, nextValue, reason) => {
              if (reason === 'reset') {
                return;
              }
              setCampaignSearchInput(nextValue);
            }}
            onChange={(_, selectedOption) => {
              setParams({ campaign: selectedOption?.value ?? undefined }, { resetPage: true });
            }}
            sx={buildFilterFieldSx(FILTER_MIN_WIDTH_CAMPAIGN)}
            renderInput={(params) => (
              <TextField
                {...params}
                size="small"
                label={t('pages:calls.filters.campaignLabel')}
                placeholder={t('pages:calls.filters.campaignSearchPlaceholder')}
                sx={filterInputSx}
              />
            )}
          />
          <Autocomplete
            multiple
            disableCloseOnSelect
            options={statusOptions}
            value={selectedStatusOptions}
            onChange={handleStatusChange}
            getOptionLabel={(option) => option.label}
            isOptionEqualToValue={(option, value) => option.value === value.value}
            renderOption={(props, option, { selected }) => (
              <Box component="li" {...props}>
                <Checkbox checked={selected} sx={{ mr: 1 }} />
                {option.label}
              </Box>
            )}
            renderInput={(params) => (
              <TextField
                {...params}
                size="small"
                label={t('pages:reports.campaigns.filters.status')}
                placeholder={t('pages:reports.campaigns.filters.statusPlaceholder')}
                sx={filterInputSx}
              />
            )}
            sx={buildFilterFieldSx(FILTER_MIN_WIDTH_STATUS)}
            slotProps={{
              chip: { size: 'small' },
            }}
          />
          <Autocomplete<ReportDateRangeOption, false, false, false>
            options={dateRangeOptions}
            value={dateRangeOption}
            isOptionEqualToValue={(option, value) => option.value === value.value}
            getOptionLabel={(option) => option.label}
            onChange={(_, selectedOption) => {
              const nextDateRange = selectedOption?.value ?? null;
              if (!nextDateRange) {
                setParams({ date_range: null, start_date: null, end_date: null }, { resetPage: true });
                return;
              }
              if (nextDateRange === 'period') {
                setParams({ date_range: nextDateRange }, { resetPage: true });
                return;
              }
              setParams({ date_range: nextDateRange, start_date: null, end_date: null }, { resetPage: true });
            }}
            sx={buildFilterFieldSx(FILTER_MIN_WIDTH_DATE_RANGE)}
            renderInput={(params) => (
              <TextField
                {...params}
                size="small"
                label={t('pages:calls.filters.dateRangeLabel')}
                sx={filterInputSx}
              />
            )}
          />
          {selectedDateRange === 'period' && (
            <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale={dayjsLocale}>
              <>
                <DateTimePicker
                  label={t('pages:calls.filters.fromDate')}
                  value={startDateValue}
                  onChange={(value) => {
                    const formattedDate = value ? value.format(DATE_TIME_FILTER_PARAM_FORMAT) : undefined;
                    setParams({ start_date: formattedDate }, { resetPage: true });
                  }}
                  maxDate={endDateValue ?? undefined}
                  slotProps={{
                    textField: { size: 'small', sx: { ...filterInputSx, minWidth: 180, flex: '1 1 180px' } },
                  }}
                />
                <DateTimePicker
                  label={t('pages:calls.filters.toDate')}
                  value={endDateValue}
                  onChange={(value) => {
                    const formattedDate = value ? value.format(DATE_TIME_FILTER_PARAM_FORMAT) : undefined;
                    setParams({ end_date: formattedDate }, { resetPage: true });
                  }}
                  minDate={startDateValue ?? undefined}
                  slotProps={{
                    textField: { size: 'small', sx: { ...filterInputSx, minWidth: 180, flex: '1 1 180px' } },
                  }}
                />
              </>
            </LocalizationProvider>
          )}
          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', ml: 'auto' }}>
            <ListActionButton
              startIcon={<Refresh />}
              onClick={fetchData}
            >
              {t('common:actions.refresh')}
            </ListActionButton>
            <ListActionButton
              startIcon={<Clear />}
              onClick={handleClearFilters}
            >
              {t('common:actions.clear')}
            </ListActionButton>
          </Box>
        </>
      )}
      table={table}
      tableFooter={(
        !loading && reports.length > 0 ? (
          <LinkPagination
            component="div"
            count={totalReports}
            page={page}
            rowsPerPage={rowsPerPage}
            onPageChange={(_event, nextPage) => setPage(nextPage)}
            onRowsPerPageChange={(event) => setRowsPerPage(Number(event.target.value))}
            labelRowsPerPage={t('common:pagination.rowsPerPage')}
            labelDisplayedRows={({ from, to, count }) =>
              count !== -1
                ? `${from}-${to} ${t('common:common.of')} ${count}`
                : `${from}-${to} ${t('common:common.of')} ${t('common:common.total')} >${to}`
            }
            getItemAriaLabel={(type) => {
              if (type === 'first') return t('common:pagination.firstPage');
              if (type === 'last') return t('common:pagination.lastPage');
              if (type === 'next') return t('common:pagination.nextPage');
              return t('common:pagination.previousPage');
            }}
            getPageLink={getPageLink}
            rowsPerPageOptions={[10, 25, 50, 100]}
          />
        ) : undefined
      )}
    />
  );
}
